import { headlineFor, toneFor, type StatusState } from '../data/state'

import { StateMarkerInline } from './state-marker'

/*
 * The first line on the page, and per §4.1 nothing sits above it.
 *
 * One sentence, one colour, readable at a glance — and it comes from the
 * server's own `status` field rather than being re-derived here. A second
 * derivation is a second answer, and the two would eventually disagree in front
 * of a customer during an incident. The only state this component computes is
 * the one the API cannot: what to say when the API did not answer.
 */

const TONE_TEXT = {
  positive: 'text-positive',
  caution: 'text-caution',
  critical: 'text-critical',
  unmeasured: 'text-text-muted',
} as const

export function Overall({
  state,
  detail,
}: {
  readonly state: StatusState
  /** One supporting clause. Absent on the operational case, where a second
   *  sentence would only dilute the first. */
  readonly detail?: string | undefined
}) {
  return (
    <section
      aria-labelledby="overall-status"
      /*
       * The one live region on this page, and it is narrow on purpose.
       *
       * A reader with the page open when the platform state changes gets no
       * notification otherwise — the poll rewrites the page in silence every
       * thirty seconds. `polite` on the headline announces the one sentence
       * that matters, and it cannot become chatter because the headline text
       * changes only when the platform's overall state does: a poll returning
       * the same state rewrites the same string and announces nothing.
       *
       * Deliberately **not** on the component or region lists. Those change for
       * reasons the headline already summarises, and four rows re-announcing on
       * every transition is the noise `make frontend-audit`'s live-region sweep
       * exists to catch.
       */
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <StateMarkerInline state={state} />
        <div>
          <h2
            id="overall-status"
            className={`text-xl font-medium ${TONE_TEXT[toneFor(state)]}`}
          >
            {headlineFor(state)}
          </h2>
          {detail === undefined || detail === '' ? null : (
            <p className="mt-1 text-sm text-text-secondary">{detail}</p>
          )}
        </div>
      </div>
    </section>
  )
}
